import {
  createContext,
  useContext,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  useSyncExternalStore,
} from "react";
import { createPortal } from "react-dom";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAtom } from "jotai";
import { AnimatePresence, motion, useReducedMotion } from "motion/react";

import { AppSidebar } from "@/components/AppSidebar";
import CbosProjectPicker from "@/cbos/CbosProjectPicker";
import { CBOS_ENTRY_PATH, showsCbosEntry } from "@/cbos/cbosAccess";
import { SiteHeader } from "@/components/SiteHeader";
import { playAuthEntrance } from "@/website/auth/authEntrance";
import OverlayBotChat from "@/components/overlay-bot/OverlayBotChat";
import {
  AnimatedSidebar,
  AnimatedSidebarClose,
  AnimatedSidebarContent,
  AnimatedSidebarHeader,
  AnimatedSidebarInset,
  AnimatedSidebarMenu,
  AnimatedSidebarMenuButton,
  AnimatedSidebarMenuItem,
  AnimatedSidebarProvider,
  useAnimatedSidebar,
} from "@/components/motion/animated-sidebar";
import { BookCheck, Blocks, LayoutDashboard } from "lucide-react";
import { SidebarCollapseIcon } from "@/components/ui/sidebar";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { TooltipProvider } from "@/components/ui/tooltip";
import { BRAND } from "@/brand/brand";
import { useCustomerLogo } from "@/hooks/useCustomerLogo";
import { useIsMobile } from "@/hooks/use-mobile";
import {
  SettingsNavigation,
  useSettingsNavigation,
} from "@/hooks/useSettingsNavigation";
import { cn } from "@/lib/utils";
import {
  consumeChatHandoff,
  shouldOpenAssistantForHandoff,
  useChatHandoff,
} from "@/guide/chatHandoff";
import {
  ASSISTANT_PANEL_CLASS,
  ASSISTANT_PANEL_ID,
  assistantPanelWidth,
} from "@/components/overlay-bot/assistantPanelChrome";
import GuideSpotlight from "@/guide/GuideSpotlight";
import AssistantMorphOverlay from "@/guide/morph/AssistantMorphOverlay";
import {
  cancelAssistantMorph,
  isMorphFor,
  useAssistantMorph,
} from "@/guide/morph/assistantMorph";
import {
  MORPH_PLAIN_FADE_MS,
  MORPH_REDUCED_CROSSFADE_MS,
  PANEL_CLOSE_MS,
  PANEL_CONTENT_DELAY_MS,
  PANEL_CONTENT_IN_MS,
  PANEL_CONTENT_OUT_MS,
  PANEL_CONTENT_SHIFT_PX,
  PANEL_EASE,
  PANEL_OPEN_MS,
  toSeconds,
} from "@/guide/morph/morphTimeline";
import { authenticationAtom } from "../State";
import { myPlatformRolesAtom, useLoadMyPlatformRoles } from "../PlatformRolesAPI";
import { useBackgroundJobWatcher } from "../BackgroundJobsAPI";
import { RULEBOOK_PATH } from "../rulebook/rulebookPath";
import DocumentViewerDock from "../documentViewer/DocumentViewerDock";
import { useDocumentViewerDock } from "../documentViewer/DocumentViewerContext";

/** Routen, die im AppShell laufen — Protected blendet dort den schwebenden
 *  Overlay-Assistenten aus: auf den Sidebar-Seiten dockt das "Fragen"-Panel
 *  dieselbe Konversation rechts an, auf /chatbot ist die Seite selbst der
 *  Assistent. */
export const APP_SHELL_PATHS = [
  "/settings",
  "/entity",
  "/administration",
  "/cbos",
  "/chatbot",
  "/guidelines",
];

/** id des Slot-Divs in der Chat-Sidebar (Ziel der Konversationsliste). */
export const CHAT_SIDEBAR_SLOT_ID = "chat-sidebar-slot";

/**
 * Ziel-Element für die Konversationsliste des Chats.
 *
 * - `undefined`: kein AppShell in der Umgebung — `ChatBot` rendert seine Liste
 *   wie früher als eigene Spalte an Ort und Stelle.
 * - `null`: Shell vorhanden, der Slot steckt aber gerade nicht im DOM (mobil
 *   hängt die Sidebar als Blatt am Body und ist erst nach dem ersten Effekt
 *   da) — die Liste entfällt dann, statt in den Chat zurückzufallen.
 * - Element: Portal-Ziel.
 */
const ChatSidebarSlotContext = createContext<HTMLElement | null | undefined>(undefined);

export function useChatSidebarSlot() {
  return useContext(ChatSidebarSlotContext);
}

/**
 * Sidebar-Rahmen des Chats im open-agents-Look: ein bündiges Panel auf
 * `--oa-panel` mit haarfeiner Trennlinie nach rechts, oben das Kundenlogo,
 * darunter der Slot. Die Konversationsliste selbst bleibt in `ChatBot` — sie
 * hängt an dessen Zustand (laufende Läufe, Filter, Auswahl) und wird nur
 * hierher portiert.
 *
 * Unter dem Logo steht „Zum Dashboard“ als Menüzeile — das Gegenstück zum
 * „Zum Assistenten“-Eintrag der Settings-Seitenleiste.
 */
function ChatSidebar({ slotRef }: { slotRef: (node: HTMLDivElement | null) => void }) {
  const { isMobile, open, setOpen } = useAnimatedSidebar();
  const { logo: customerLogo, isFallback: isFallbackLogo } = useCustomerLogo();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [authentication] = useAtom<any>(authenticationAtom);
  const [myPlatformRoles] = useAtom(myPlatformRolesAtom);
  const collapsed = !open && !isMobile;

  return (
    // Wie die Settings-Seitenleiste: eingeklappt bleibt eine Symbolleiste
    // stehen (Logo-Kachel, Dashboard, Neuer Chat, Suchen — Letztere kommen
    // aus dem `ChatBot`-Slot); die Konversationsliste blendet dort aus.
    <AnimatedSidebar
      collapsible="icon"
      variant="sidebar"
      className="bg-[var(--oa-panel)]"
      panelClassName="border-r-[color:var(--oa-border)] bg-[var(--oa-panel)]"
    >
      <AnimatedSidebarHeader className="p-2">
        {/* Baugleich mit dem Sidebar-Kopf der Settings-Seiten (`AppSidebar`):
            gleiche Zeilenhöhe (h-12 wie die Kopfzeile), gleiche Logogröße,
            gleicher Schriftzug; in der Symbolleiste überblendet der Schriftzug
            zur quadratischen Logo-Kachel — die hier die Leiste wieder öffnet. */}
        <div
          className={cn(
            "relative -mx-2 flex h-12 shrink-0 items-center gap-1 px-2",
            // Eine Wortmarke füllt die Zeile fast ganz aus. Ohne das negative
            // obere Außenmaß behält sie das Polster des Kopfbereichs über sich
            // und bekommt unten dasselbe dazu, statt zwischen Rand und erstem
            // Eintrag eingeklemmt zu sitzen.
            BRAND.logoIncludesName ? "mb-2" : "-mt-2",
          )}
        >
          <div
            aria-hidden={collapsed}
            className={cn(
              "flex min-w-0 flex-1 items-center gap-2 overflow-hidden rounded-md transition-opacity duration-200",
              // Die Wortmarke beginnt mit einem Buchstabenstamm ohne eigene
              // Randzone — sie braucht mehr Einzug als das Monogramm, sonst
              // klebt sie am Rand der Leiste.
              BRAND.logoIncludesName ? "py-0 pl-3 pr-1.5" : "p-1.5",
              collapsed && "pointer-events-none opacity-0",
            )}
          >
            <img
              src={customerLogo}
              alt={isFallbackLogo && BRAND.logoIncludesName ? BRAND.name : ""}
              height={BRAND.logoIncludesName ? 44 : 32}
              data-testid="app-brand-logo"
              className={cn(
                "w-auto shrink-0 object-contain",
                // Die Kopfzeile ist h-12 (48px) und bleibt es — sie liegt auf
                // einer Linie mit der Kopfzeile rechts. 44px ist damit die
                // größte Marke, die noch mit etwas Luft hineinpasst.
                BRAND.logoIncludesName ? "h-11 max-w-[15rem]" : "h-8 max-w-[11rem]",
              )}
            />
            {isFallbackLogo && !BRAND.logoIncludesName ? (
              <span className="truncate text-base font-semibold">{BRAND.name}</span>
            ) : null}
          </div>
          <button
            type="button"
            aria-hidden={!collapsed}
            tabIndex={collapsed ? undefined : -1}
            aria-label={t("header.toAssistant")}
            onClick={() => setOpen(true)}
            data-testid="app-brand-logo-square"
            className={cn(
              "absolute left-1/2 top-1/2 flex size-9 -translate-x-1/2 -translate-y-1/2 items-center justify-center overflow-hidden rounded-lg border border-border/60 bg-background outline-none transition-opacity duration-200 focus-visible:ring-2 focus-visible:ring-ring",
              collapsed ? "opacity-100" : "pointer-events-none opacity-0",
            )}
          >
            {/* Ein Kundenlogo wird auf sein linkes Ende beschnitten — dort
                sitzt bei den meisten Lockups die Bildmarke. Eine Marke mit
                eigenem quadratischem Zeichen zeigt stattdessen dieses,
                vollständig und mit etwas Luft. */}
            <img
              src={isFallbackLogo ? BRAND.squareLogo : customerLogo}
              alt=""
              className={cn(
                "h-full w-full",
                isFallbackLogo ? "object-contain p-1" : "object-cover object-left",
              )}
            />
          </button>
          {isMobile ? (
            <AnimatedSidebarClose className="size-7 shrink-0 rounded-md text-[var(--oa-fg-3)] transition-colors hover:bg-[var(--oa-surface)] hover:text-[var(--oa-fg)] [&_svg]:size-4">
              <SidebarCollapseIcon />
            </AnimatedSidebarClose>
          ) : null}
        </div>
        {/* Gegenstück zum „Zum Assistenten"-Eintrag der Settings-Seitenleiste:
            gleiche Menüzeile, gleicher Rahmen — nur in die andere Richtung.
            CBOS steht daneben, wo es den Mandanten gibt: derselbe Rang, weil es
            ein eigener Bereich ist und keine Unterseite des Dashboards. */}
        <AnimatedSidebarMenu className="px-1">
          <AnimatedSidebarMenuItem>
            <AnimatedSidebarMenuButton
              icon={<LayoutDashboard size={16} />}
              onSelect={() => navigate("/settings")}
              className="border border-border/60"
            >
              {t("header.toDashboard")}
            </AnimatedSidebarMenuButton>
          </AnimatedSidebarMenuItem>
          {showsCbosEntry(authentication?.customer) ? (
            <AnimatedSidebarMenuItem>
              <AnimatedSidebarMenuButton
                icon={<Blocks size={16} />}
                onSelect={() => navigate(CBOS_ENTRY_PATH)}
                className="border border-border/60"
              >
                {t("header.cbos")}
              </AnimatedSidebarMenuButton>
            </AnimatedSidebarMenuItem>
          ) : null}
          {myPlatformRoles && myPlatformRoles.length > 0 ? (
            <AnimatedSidebarMenuItem>
              <AnimatedSidebarMenuButton
                icon={<BookCheck size={16} />}
                onSelect={() => navigate(RULEBOOK_PATH)}
                className="border border-border/60"
              >
                {t("header.rulebook")}
              </AnimatedSidebarMenuButton>
            </AnimatedSidebarMenuItem>
          ) : null}
        </AnimatedSidebarMenu>
      </AnimatedSidebarHeader>
      {/* Der Slot füllt den Rest der Sidebar; gescrollt wird innerhalb der
          Liste (je Abschnitt), deshalb steht hier `overflow-hidden` statt des
          Standard-Scrollers — `oa-scroll` hält den dezenten Balken bereit. */}
      <AnimatedSidebarContent className="oa-scroll gap-0 overflow-hidden p-0">
        <div
          id={CHAT_SIDEBAR_SLOT_ID}
          ref={slotRef}
          className="flex min-h-0 flex-1 flex-col"
        />
      </AnimatedSidebarContent>
    </AnimatedSidebar>
  );
}

/**
 * The window width as a **subscribed** value. Reading `window.innerWidth` during
 * render would be a snapshot without a subscription: when the window changes,
 * nothing re-renders — the number motion animates the panel's width to (and
 * remembers as its last value) would stay stale while `max-w-[40vw]` has long
 * since drawn the panel narrower. On close, motion would then set the old width
 * inline in the first frame: a jump outwards before it slides in.
 *
 * `useSyncExternalStore` returns a number — a stable snapshot, not a new object
 * per call, so no infinite loop.
 */
function subscribeViewportWidth(onStoreChange: () => void) {
  window.addEventListener("resize", onStoreChange);
  return () => window.removeEventListener("resize", onStoreChange);
}

function getViewportWidth() {
  return window.innerWidth;
}

/** Without a window (server/prerender): the width the shell assumes. */
const VIEWPORT_WIDTH_FALLBACK = 1280;

export interface AppShellContext extends SettingsNavigation {
  setTitle: (title: string) => void;
}

/**
 * Gemeinsames Layout der Sidebar-Seiten (Layout-Route mit Outlet): Die
 * Sidebar und ihr Zustand bleiben beim Routenwechsel stehen, statt pro Seite
 * neu aufgebaut zu werden. Kindrouten setzen ihren Kopfzeilentitel über den
 * Outlet-Context (`setTitle`) und erhalten dieselben Navigations-/Zugriffs-
 * Daten, ohne sie doppelt zu laden. Auf /chatbot tritt an die Stelle der
 * Bereichsliste die Konversationsliste des Chats (siehe `ChatSidebar`); das
 * "Fragen"-Panel entfällt dort, weil der Chat selbst der Assistent ist.
 */
export default function AppShell() {
  const { t } = useTranslation();
  const navigation = useSettingsNavigation();
  const [shellAuthentication] = useAtom<any>(authenticationAtom);
  useLoadMyPlatformRoles(shellAuthentication?.email);
  // Hintergrundjobs (LV aus Anlagenliste): Toast mit Link ins Gespräch, wenn einer fertig wird.
  const shellNavigate = useNavigate();
  useBackgroundJobWatcher(
    shellAuthentication?.customer,
    {
      done: (title) => t("backgroundJob.toastDone", { title }),
      failed: (title) => t("backgroundJob.toastFailed", { title }),
      open: t("backgroundJob.openConversation"),
    },
    (conversationId) => shellNavigate(`/chatbot?conversationId=${encodeURIComponent(conversationId)}`),
  );
  const [title, setTitle] = useState("");
  const [assistantOpen, setAssistantOpen] = useState(false);
  /**
   * **How** the panel is currently arriving and leaving. Only the „Fragen"
   * button slides; the handoff from the main chat (A10/A12) must not, because
   * there the flying shell already does the movement — two movements on top of
   * each other would be the exact opposite of "clean". The state depends on the
   * *cause*, not the timing: it is set where visibility changes and stays until
   * the next change, so the slide-out (`exit`) speaks the same language as the
   * slide-in.
   */
  const [panelMotion, setPanelMotion] = useState<"slide" | "instant">("instant");
  const [chatSidebarSlot, setChatSidebarSlot] = useState<HTMLDivElement | null>(null);
  const isMobile = useIsMobile();
  const viewportWidth = useSyncExternalStore(
    subscribeViewportWidth,
    getViewportWidth,
    () => VIEWPORT_WIDTH_FALLBACK,
  );
  const location = useLocation();
  const reduceMotion = useReducedMotion() ?? false;
  /** Handoff from the main chat — see `src/guide/chatHandoff.ts`. */
  const handoff = useChatHandoff();
  /** The running morph — see `src/guide/morph/`. */
  const morph = useAssistantMorph();
  /** The flight's target: the real `aside`. The overlay measures it. */
  const asideRef = useRef<HTMLElement | null>(null);
  // Der Kaskaden-Anker `settingsPage` (App.sass) gilt nur auf /settings —
  // andere Shell-Seiten sollen die dort verschachtelten Alt-Selektoren
  // (z. B. .companyPanel, .imageArea) nicht erben.
  const onSettings = location.pathname === "/settings";
  const onChat = location.pathname.startsWith("/chatbot");
  const address = `${location.pathname}${location.search}`;

  /**
   * Derived **during render**, not set in an effect — this is a hard
   * requirement of the morph, not a nicety. The handoff and `navigate` happen in
   * the same event handler, so React batches them into *one* update: in exactly
   * this commit the chat surface is torn down **and** the `aside` is mounted
   * (invisible, `opacity: 0`).
   *
   * Three things depend on this:
   * 1. The target page is in its **final geometry** from the first frame — at
   *    the end of the flight nothing reflows any more, there is no jump.
   * 2. The overlay finds its target rect immediately instead of waiting for a
   *    second commit.
   * 3. Mounting and adoption reconciliation of the panel (`OverlayBotChat` sets
   *    the whole history when adopting) happen **behind** the shell, not in the
   *    middle of the visible flight.
   *
   * If visibility lived in a passive effect, the panel would arrive one commit
   * and one paint later — and the white frame the whole design avoids would be
   * back. `assistantOpen` is synced right below so the panel stays open once
   * the handoff has been consumed.
   */
  const handoffOpens = shouldOpenAssistantForHandoff(handoff, onChat, address);

  /**
   * And `assistantOpen` is synced **in the same render**, not in an effect.
   * That is the difference between "the conversation comes along" and an empty
   * panel:
   *
   * `handoffOpens` only holds the panel open while the handoff is pending — and
   * it gets consumed **earlier** than an effect here could set `assistantOpen`.
   * `OverlayBotChat` picks it up in its own effect, and child effects run before
   * the parent's. Picking it up changes module state, `useChatHandoff` hangs off
   * `useSyncExternalStore`, and its re-render runs **synchronously** — React
   * flushes it at the end of the same effect pass in which the parent's
   * `setAssistantOpen(true)` had only just been queued. In exactly that render
   * `handoffOpens` was already `false` and `assistantOpen` still `false`: the
   * `aside` disappeared for one commit — and with it the `OverlayBotChat`
   * instance in which both the state *and* the adoption marker live. The panel
   * then came back empty, with the opening bubble and no history; the handoff
   * was consumed, it did not apply a second time, and the history fetch's
   * response went nowhere.
   *
   * So the state is adjusted during render — the documented pattern for
   * derived state (React: "Adjusting some state when a prop changes"): React
   * re-renders `AppShell` immediately, **before** the children commit.
   * `assistantOpen` is therefore already set in the commit that mounts the
   * panel, and no pickup, however fast, can tear it down afterwards. The
   * one-shot behaviour is keyed on the handoff's `id`, not a boolean flag: a
   * user who then collapses the panel should get it closed; only the **next**
   * handoff opens it again.
   */
  const [openedForHandoff, setOpenedForHandoff] = useState<string | null>(null);
  if (handoffOpens && handoff && openedForHandoff !== handoff.id) {
    setOpenedForHandoff(handoff.id);
    setAssistantOpen(true);
    // The handoff brings its own choreography (shell → crossfade).
    setPanelMotion("instant");
  }

  const assistantVisible = assistantOpen || handoffOpens;

  /**
   * Ein Dokument steht rechts angedockt (`DocumentViewerDock`). Ist dazu das „Fragen"-Panel offen,
   * tritt die Seite zurück: Chat links, Dokument rechts — drei Spalten ließen auf einem
   * 14-Zoll-Bildschirm keiner genug Platz. Ausgeblendet, nicht abgebaut, damit die Seite beim
   * Schließen so dasteht, wie sie war.
   */
  const documentDocked = Boolean(useDocumentViewerDock()?.open) && !isMobile;
  const pageYieldsToDocument = documentDocked && assistantVisible && !onChat;

  /**
   * Is a shell currently flying here? Then the real `aside` is invisible,
   * unfocusable and removed from the screen-reader tree — otherwise
   * `OverlayBotChat` would pull focus into an invisible `textarea` on mount (it
   * focuses when `active`).
   *
   * Bound to the **address** (`isMorphFor`, the same folding as handoff and
   * self-navigation): a flight announced for somewhere else must not hide
   * anything here — not even in the reverse direction (jumping from a panel
   * page to `/chatbot`).
   */
  const morphHere = morph !== null && !onChat && morph.decision.morph && isMorphFor(morph, address);
  const morphing = morphHere && morph!.phase === "flying";

  /**
   * Is the panel currently sliding? Three conditions, all three required:
   * the cause was the button, no shell is flying here, and motion has not been
   * opted out of. With `prefers-reduced-motion` it stays a short crossfade
   * (`MORPH_REDUCED_CROSSFADE_MS`) — the same number as in the morph.
   */
  const panelSlides = panelMotion === "slide" && !morphHere && !reduceMotion;
  /**
   * The target width in pixels. motion needs a number (see
   * `assistantPanelWidth`); `max-w-[40vw]` still caps it in the browser, this
   * calculation only ensures the animation does not run into the cap and stand
   * still for the last frames. It depends on the **subscribed** window size
   * (see `subscribeViewportWidth`) so the value motion remembers is always the
   * one the panel actually sits at.
   */
  const panelWidth = assistantPanelWidth(viewportWidth);

  /**
   * The „Fragen" button. **Closing** during a running flight cancels the
   * morph: the shell fades out over `MORPH_CANCEL_FADE_MS` instead of flying
   * towards a panel that is about to disappear. And the handoff is consumed:
   * while it is pending, `handoffOpens` holds the panel open —
   * `setAssistantOpen(false)` alone would visibly do nothing.
   */
  const toggleAssistant = () => {
    // Toggle against the *visible* state, not against `assistantOpen`:
    // during a pending handoff the panel is already open although the state
    // is still `false`.
    const closing = assistantVisible;
    if (closing) {
      if (morph) cancelAssistantMorph();
      // The panel that was supposed to adopt it is closing — leaving the
      // handoff pending here would keep the panel open, and the next opening
      // would bring a history nobody expects any more.
      consumeChatHandoff();
    }
    setPanelMotion(reduceMotion ? "instant" : "slide");
    setAssistantOpen(!closing);
  };

  /**
   * The target page's fade-in class belongs to the **route instance**, not to
   * the morph state. The container carries `key={location.pathname}`; if its
   * class switched from `guide-morph-page-enter` to `animate-in fade-in-0` when
   * the morph ends, the fade-in would start a second time on the same DOM node
   * — the finished target page would jump to opacity 0 and come back over
   * 300 ms, exactly in the frame in which the shell disappears. So decide once
   * per path and then keep it.
   *
   * `motion-reduce:animate-none` on the branch without a morph: with
   * `prefers-reduced-motion` no morph is created at all (`shouldMorphNow`), so
   * *this* branch always runs — and a 300 ms fade-in exceeds the 150 ms limit.
   */
  const pageEnterRef = useRef<{ pathname: string; className: string } | null>(null);
  const pageEnter =
    pageEnterRef.current && pageEnterRef.current.pathname === location.pathname
      ? pageEnterRef.current
      : {
          pathname: location.pathname,
          className: morphHere
            ? "guide-morph-page-enter"
            : "animate-in fade-in-0 duration-300 motion-reduce:animate-none",
        };
  pageEnterRef.current = pageEnter;

  /**
   * Geltungsbereich der Chat-Seite (helle open-agents-Optik) — am `body`,
   * nicht am Wrapper des Shells: Radix rendert Popover, Menüs und Sheets als
   * Portale in den Body, und die dortigen Knoten erben die Werte eines
   * Wrappers nicht. Am Body gelten sie für beides. Der Aufräumschritt nimmt
   * die Klasse beim Routenwechsel wieder ab.
   */
  useEffect(() => {
    if (!onChat) return;
    const { body } = document;
    body.classList.add("chat-oa");
    return () => body.classList.remove("chat-oa");
  }, [onChat]);

  /**
   * A flight that does not belong here is cleaned up: foreign navigation, back
   * button, sidebar click — and the case where the target renders no shell at
   * all. Without this a record plus its DOM clone would be left behind, and
   * `whenMorphIdle()` (the grey „mitgekommen" line) would wait for it to end.
   * The module's deadline (`MORPH_TTL_MS`) catches the rest.
   */
  useEffect(() => {
    if (morph && !isMorphFor(morph, address)) cancelAssistantMorph();
  }, [morph, address]);

  // Right after signing in, the shell builds itself up once (authEntrance.css).
  // Ends with the first route change (the cleanup runs before that page paints).
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useLayoutEffect(() => playAuthEntrance(), [location.pathname]);

  return (
    // Radix-Tooltips (Einstellungs-Panels, Objektseiten) brauchen einen
    // Provider über sich — bis hierher stellte ihn der shadcn-
    // `SidebarProvider`; die Animated-Sidebar bringt keinen mit.
    <TooltipProvider delayDuration={0}>
      <AnimatedSidebarProvider>
        {onChat ? (
          <ChatSidebar slotRef={setChatSidebarSlot} />
        ) : (
          <AppSidebar
            items={navigation.items}
            selectedKey={navigation.selectedPanel}
            setItem={navigation.openPanel}
            // Nur unter /cbos: Das Projekt bestimmt dort jeden Bildschirm, anderswo gibt es keines.
            aboveGroups={location.pathname.startsWith("/cbos") ? <CbosProjectPicker /> : undefined}
          />
        )}
        <>
          <AnimatedSidebarInset
            className={cn(
              "min-w-0 overflow-x-hidden",
              onSettings && "settingsPage",
              // The chat scrolls within itself (message list) and keeps its
              // input row at the bottom edge. For that it needs a *definite*
              // height: the inset's minimum height (`min-h-svh`) is doubled
              // here as its maximum height, otherwise the page grows with its
              // content and the input row slides below the window edge. Both
              // sidebars are flush (`variant="sidebar"`), so the inset has no
              // margin — the page fills the window entirely and nothing scrolls twice.
              onChat && "max-h-svh overflow-hidden bg-[var(--oa-panel)]",
              pageYieldsToDocument && "hidden",
            )}
          >
            <SiteHeader
              title={title}
              variant={onChat ? "chat" : "default"}
              assistantOpen={onChat ? undefined : assistantVisible}
              onToggleAssistant={onChat ? undefined : toggleAssistant}
            />
            {/* Soft page transition: the header stays identical in the same
                place, only the content fades in. The key on the path re-runs
                the animation on every route change — also between the chat and
                sidebar pages, which now share the same shell. */}
            {/* Soft page transition — and during a morph its own
                choreography: the target page should *settle* under the shell,
                not run against the flight uncontrolled. Distance, duration and
                curve are defined exactly once, in `morph.css`
                (`guide-morph-page-enter`) — stating the same number here a
                second time would be the next drift. Without a morph it stays
                with the other CSS class. */}
            <div
              key={location.pathname}
              className={cn("flex min-h-0 flex-1 flex-col", pageEnter.className)}
            >
              <ChatSidebarSlotContext.Provider value={onChat ? chatSidebarSlot : undefined}>
                <Outlet context={{ ...navigation, setTitle } as AppShellContext} />
              </ChatSidebarSlotContext.Provider>
            </div>
          </AnimatedSidebarInset>
          {/* "Fragen": assistant panel docked on the right (ElevenLabs pattern).
              On desktop it pushes the content together; on mobile it comes as
              a sheet. Always capped to the window height and sticky at the top
              — scrolling happens *inside* the transcript, the input row stays
              visible (see `assistantPanelChrome`). */}
          {/* `AnimatePresence` for **one** thing only: the slide-out. Without
              it the `aside` would vanish in the commit in which
              `assistantVisible` drops to `false`, and closing would be a hard
              cut. `initial={false}` prevents a hard reload onto a page with a
              pending handoff from playing the motion — what gets animated is
              what changes *here*, not the first view. */}
          {/* On the chat page the presence is not rendered at all (28.09.2026): a panel that
              was leaving while the route changed to /chatbot stayed mounted at opacity 0 and
              400px wide — its exit never completed — and pushed the chat 400px to the left.
              The slide-out still runs everywhere else. */}
          {onChat ? null : (
          <AnimatePresence initial={false}>
            {!onChat && assistantVisible && !isMobile ? (
              <motion.aside
                key="assistant-panel"
                id={ASSISTANT_PANEL_ID}
                ref={asideRef}
                data-testid="assistant-panel"
                data-morphing={morphing ? "true" : undefined}
                data-sliding={panelSlides ? "true" : undefined}
                aria-hidden={morphing || undefined}
                // `aria-hidden` alone would only remove the panel from the
                // screen-reader tree: the `textarea` of `OverlayBotChat` would
                // stay tabbable, and a Tab press mid-flight would land in an
                // invisible field inside an `aria-hidden` subtree (Axe:
                // "aria-hidden-focus"). `inert` removes focus, pointer and
                // screen-reader tree in one go; React 18 does not know the
                // prop, spread as a string it lands as an attribute (the same
                // pattern as on the shell).
                {...(morphing ? { inert: "" } : {})}
                className={cn(ASSISTANT_PANEL_CLASS, morphing && "pointer-events-none")}
                /*
                 * Two branches, and the difference is the cause.
                 *
                 * **Handoff/morph** (`panelSlides === false`): opacity
                 * **only**. The flying shell does the movement; the panel sits
                 * at 0 during the flight and fades in over 120 ms in the
                 * crossfade. A cancel also runs over these 120 ms —
                 * `morphHere` stays true while the record is in the
                 * `cancelled` phase, and the shell fades out in the same
                 * milliseconds. No empty strip.
                 *
                 * **Button** (`panelSlides === true`): the **width**. That is
                 * deliberately the animated property rather than `transform`:
                 * the panel is a flex sibling of the page content, and the page
                 * should *move over* instead of a surface sliding across it. A
                 * translation would need a second, co-animated placeholder
                 * width — two sizes that must match to within 0.1 px, and anew
                 * on every window change. The width costs layout per frame, but
                 * only on a flex row with two children — the content inside is
                 * fixed at its target width and never wraps once (see below).
                 * Opacity stays at 1 in this branch: a panel that grows and
                 * fades in at the same time is two movements.
                 */
                initial={panelSlides ? { width: 0, opacity: 1 } : { opacity: 0 }}
                animate={
                  panelSlides
                    ? { width: panelWidth, opacity: morphing ? 0 : 1 }
                    : { opacity: morphing ? 0 : 1 }
                }
                exit={
                  panelSlides
                    ? {
                        width: 0,
                        opacity: 1,
                        transition: {
                          duration: toSeconds(PANEL_CLOSE_MS),
                          ease: PANEL_EASE,
                        },
                      }
                    : {
                        opacity: 0,
                        transition: {
                          duration: toSeconds(MORPH_REDUCED_CROSSFADE_MS),
                          ease: "linear",
                        },
                      }
                }
                transition={{
                  width: { duration: toSeconds(PANEL_OPEN_MS), ease: PANEL_EASE },
                  opacity: {
                    duration: toSeconds(
                      reduceMotion || morphHere
                        ? MORPH_REDUCED_CROSSFADE_MS
                        : MORPH_PLAIN_FADE_MS,
                    ),
                    ease: "linear",
                  },
                }}
              >
                {/* The content follows the edge instead of starting with it:
                    180 ms from 80 ms. On close it leaves first (120 ms), then
                    the edge — but **only** in the slide branch. In the
                    morph/reduced branch the `aside` alone carries the opacity:
                    an `exit` of its own on the child would run along there
                    (nested motion children inherit the exit phase via the
                    PresenceContext), and with duration 0 the content would be
                    gone in frame 0 while the shell still fades for 120 ms —
                    exactly the empty strip that must not happen. Without its
                    own `exit` the child inherits nothing and disappears with
                    the parent: one picture, one operation.

                    The fixed width is the second part of this. Without it the
                    width of this layer is the content width of the `aside`,
                    i.e. the *animated* one — transcript, bubbles and input box
                    would re-wrap at every intermediate width for 300 ms, with
                    opacity visible from 80 ms. With it only the viewport moves
                    (`overflow-hidden` is on the `aside`): pure clipping, not a
                    single re-wrap, per frame only the layout of the flex row.

                    The hairline (`border-l`) sits on the `aside` — i.e. on the
                    box whose width is animating. That is the deliberate
                    exception to the guardrail in `morphTimeline.ts`, and it
                    holds: that one is about surfaces scaled via `transform`,
                    where a 1 px border frays. Here the **layout** width
                    changes; the border stays exactly 1 px and stands still at
                    the left edge. What remains is that at `width: 0`
                    (border-box) 1 px stays visible — until `AnimatePresence`
                    removes the node, which happens at the end of the same
                    run-out. */}
                <motion.div
                  data-testid="assistant-panel-content"
                  className="flex min-h-0 flex-1 flex-col"
                  style={panelSlides ? { width: panelWidth } : undefined}
                  initial={panelSlides ? { opacity: 0, y: PANEL_CONTENT_SHIFT_PX } : false}
                  animate={{ opacity: 1, y: 0 }}
                  exit={
                    panelSlides
                      ? {
                          opacity: 0,
                          transition: {
                            duration: toSeconds(PANEL_CONTENT_OUT_MS),
                            ease: PANEL_EASE,
                          },
                        }
                      : undefined
                  }
                  transition={{
                    delay: toSeconds(PANEL_CONTENT_DELAY_MS),
                    duration: toSeconds(PANEL_CONTENT_IN_MS),
                    ease: PANEL_EASE,
                  }}
                >
                  <OverlayBotChat active={assistantVisible && !morphing} />
                </motion.div>
              </motion.aside>
            ) : null}
          </AnimatePresence>
          )}
          {/* Das geöffnete Dokument, rechts neben Chat bzw. „Fragen"-Panel. */}
          <DocumentViewerDock viewportWidth={viewportWidth} />
          {/* Mobile: Radix' Sheet brings its own fade in/out — nothing is
              animated a **second** time here. Only the button state in the
              header changes, as on desktop. */}
          {!onChat && isMobile ? (
            <Sheet open={assistantVisible} onOpenChange={setAssistantOpen}>
              <SheetContent
                side="right"
                id={ASSISTANT_PANEL_ID}
                data-testid="assistant-sheet"
                // As on desktop: capped to the window height, the list scrolls
                // inside. `inset-y-0` already makes the sheet window-high; the
                // cap keeps it there if a legacy rule resolves `h-full`.
                className="assistant-panel-viewport flex w-full flex-col gap-0 overflow-hidden p-0 sm:max-w-md"
              >
                {/* No visible header any more (as on desktop) — but the title
                    stays in the tree: Radix' Dialog requires a `Title` for the
                    sheet, otherwise screen readers lack the name and the
                    library warns. `sr-only` only removes it visually. */}
                <SheetHeader className="sr-only">
                  <SheetTitle>{t("overlayBot.title")}</SheetTitle>
                </SheetHeader>
                <OverlayBotChat active={assistantVisible} />
              </SheetContent>
            </Sheet>
          ) : null}
          {/* The flying shell. Deliberately on `body` and not as a sibling
              here: `position: fixed` loses its reference as soon as an
              ancestor sets `transform`/`filter`/`contain` (the animated
              sidebar does), and `overflow-x-hidden` on the inset would clip. */}
          {morphHere && assistantVisible && morph
            ? createPortal(
                <AssistantMorphOverlay morph={morph} targetRef={asideRef} />,
                document.body,
              )
            : null}
        </>
      </AnimatedSidebarProvider>
      {/* The assistant's pointer — **once** for the whole shell and
          deliberately here: outside the container with `key={location.pathname}`
          (which would recreate it on every route change, i.e. exactly when it
          starts working) and outside the animated sidebar, which sets
          `transform` — `position: fixed` would lose its reference to the
          window inside it. `TooltipProvider` itself renders no element. */}
      <GuideSpotlight />
    </TooltipProvider>
  );
}
